import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import Button from '@material-ui/core/Button'
import { getVideoFileError } from 'lib/cloudinary/upload-video'

// Browsers need the MIME types here (not just `video/*`) so the picker only
// offers formats `getVideoFileError` accepts. iOS lists camera roll videos as
// `video/quicktime`.
const ACCEPT = 'video/mp4,video/quicktime,video/webm'

/**
 * A button that opens the native video picker and validates each chosen file
 * with `getVideoFileError` (type and 100MB limit).
 *
 * `onSelect({ files, errors })` receives the valid files and one
 * `{ file, message }` entry per rejected file. The input is cleared after every
 * pick so the same file can be chosen again.
 */
const VideoFilePicker = ({
  onSelect,
  multiple = false,
  disabled = false,
  label,
  inputTestId,
  inputId,
  describedBy,
  variant = 'outlined',
  color = 'primary',
  startIcon,
  endIcon,
  size,
  fullWidth = false,
}) => {
  const inputRef = useRef(null)

  const handleChange = event => {
    const picked = Array.from(event.target.files || [])
    // Clear so choosing the same file again still fires `change`.
    event.target.value = ''
    if (picked.length === 0) return

    const files = []
    const errors = []
    picked.forEach(file => {
      const message = getVideoFileError(file)
      if (message) {
        errors.push({ file, message })
      } else {
        files.push(file)
      }
    })
    onSelect({ files, errors })
  }

  return (
    <>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={ACCEPT}
        multiple={multiple}
        hidden
        onChange={handleChange}
        data-testid={inputTestId}
        aria-describedby={describedBy}
      />
      <Button
        type="button"
        variant={variant}
        color={color}
        disabled={disabled}
        fullWidth={fullWidth}
        startIcon={startIcon}
        endIcon={endIcon}
        size={size}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
    </>
  )
}

VideoFilePicker.propTypes = {
  onSelect: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.node.isRequired,
  inputTestId: PropTypes.string,
  inputId: PropTypes.string,
  describedBy: PropTypes.string,
  variant: PropTypes.string,
  color: PropTypes.string,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  size: PropTypes.string,
  fullWidth: PropTypes.bool,
}

export default VideoFilePicker
